import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Paper, CircularProgress, Chip, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Select, FormControl, InputLabel, Alert, Tabs, Tab, Collapse, Menu,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import InputIcon from '@mui/icons-material/Input';
import OutputIcon from '@mui/icons-material/Output';
import { useNavigate } from 'react-router-dom';
import { blockedStockService } from '../services/api';
import api from '../services/api';
import { navigateWithReturn, stockReturnLabel, stockReturnTo } from '../utils/pageBack';

const STATUS_LABELS = {
  blocked: { label: 'Заблоковано', color: 'error' },
  released: { label: 'Допущено', color: 'success' },
  temp_released: { label: 'Тимчасовий допуск', color: 'info' },
  scrapped: { label: 'Списано', color: 'default' },
  reworked: { label: 'На доробку', color: 'warning' },
};

function formatDt(v) {
  if (!v) return '—';
  const d = new Date(v);
  return d.toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function readStoredPerms() {
  try {
    return JSON.parse(localStorage.getItem('currentUser') || 'null')?.effective_permissions || {};
  } catch {
    return {};
  }
}

export default function BlockingStockPage({ currentUser }) {
  const navigate = useNavigate();
  const perms = currentUser?.effective_permissions || readStoredPerms();
  const canResolve = perms['menu.stock.blocking.resolve'] === true;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // За замовчуванням без фільтра статусу (усі записи); при виході зі сторінки state скидається з unmount
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('created');
  const [sortDir, setSortDir] = useState('desc');
  const [tab, setTab] = useState(0);
  const [resolveDialog, setResolveDialog] = useState(null);
  const [resolveForm, setResolveForm] = useState({
    action: 'unlock',
    resolution_note: '',
    good_qty: '',
    scrap_qty: '',
  });
  const [saving, setSaving] = useState(false);
  const [journalRows, setJournalRows] = useState([]);
  const [journalLoading, setJournalLoading] = useState(false);

  // Header buttons state
  const [shortageOpen, setShortageOpen] = useState(false);
  const [shortages, setShortages] = useState([]);
  const [shortageLoading, setShortageLoading] = useState(false);
  const [refsAnchorEl, setRefsAnchorEl] = useState(null);
  const refsMenuOpen = Boolean(refsAnchorEl);
  const [infoDialog, setInfoDialog] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await blockedStockService.getAll(params);
      const data = res.data;
      setRows(Array.isArray(data) ? data : (data.results || []));
    } catch (e) {
      console.error(e);
      alert('Не вдалося завантажити склад блокування');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const loadJournal = useCallback(async () => {
    try {
      setJournalLoading(true);
      const res = await blockedStockService.journal();
      const data = res.data;
      setJournalRows(Array.isArray(data) ? data : (data.results || []));
    } catch (e) {
      console.error(e);
      setJournalRows([]);
    } finally {
      setJournalLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 1) loadJournal();
  }, [tab, loadJournal]);

  const openResolve = (row) => {
    setResolveDialog(row);
    setResolveForm({
      action: 'unlock',
      resolution_note: '',
      good_qty: '',
      scrap_qty: '',
    });
  };

  const submitResolve = async () => {
    if (!resolveDialog) return;
    const action = resolveForm.action;
    if (action === 'resort') {
      const g = parseInt(resolveForm.good_qty, 10);
      const s = parseInt(resolveForm.scrap_qty, 10);
      if (Number.isNaN(g) || Number.isNaN(s) || g + s !== Number(resolveDialog.quantity)) {
        alert(`Сума придатної і браку має = ${resolveDialog.quantity}`);
        return;
      }
    }
    try {
      setSaving(true);
      const payload = {
        action,
        resolution_note: resolveForm.resolution_note,
      };
      if (action === 'resort') {
        payload.good_qty = parseInt(resolveForm.good_qty, 10);
        payload.scrap_qty = parseInt(resolveForm.scrap_qty, 10);
      }
      await blockedStockService.resolve(resolveDialog.id, payload);
      setResolveDialog(null);
      await load();
      if (tab === 1) await loadJournal();
    } catch (e) {
      const msg = e.response?.data?.detail || JSON.stringify(e.response?.data) || e.message;
      alert(msg || 'Помилка');
    } finally {
      setSaving(false);
    }
  };

  const approveShip = async (row) => {
    if (!row?.id) return;
    if (!window.confirm(`Погодити відвантаження «${row.product_name}» (${row.quantity} шт)?`)) return;
    try {
      setSaving(true);
      await blockedStockService.approveShip(row.id, { note: 'Погоджено відвантаження ВКЯ' });
      await load();
      if (tab === 1) await loadJournal();
    } catch (e) {
      const msg = e.response?.data?.detail || JSON.stringify(e.response?.data) || e.message;
      alert(msg || 'Помилка погодження');
    } finally {
      setSaving(false);
    }
  };

  const q = search.trim().toLowerCase();
  const filteredRows = (() => {
    let data = q ? rows.filter((r) =>
      (r.product_name || '').toLowerCase().includes(q) ||
      (r.product_article || '').toLowerCase().includes(q)
    ) : [...rows];
    data.sort((a, b) => {
      let va, vb;
      if (sortKey === 'name') { va = a.product_name || ''; vb = b.product_name || ''; }
      else if (sortKey === 'quantity') { va = a.quantity; vb = b.quantity; }
      else { va = a.created_at || ''; vb = b.created_at || ''; }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  })();

  return (
    <Box sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Блокування</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant={shortageOpen ? 'contained' : 'outlined'} color="warning" startIcon={<WarningAmberIcon />}
            onClick={() => {
              if (!shortageOpen) {
                setShortageLoading(true);
                api.get('/material-shortages/').then(r => setShortages(r.data || [])).catch(() => setShortages([])).finally(() => setShortageLoading(false));
              }
              setShortageOpen(o => !o);
            }}>
            Нестача {shortages.length > 0 && !shortageOpen ? `(${shortages.length})` : ''}
          </Button>
          <Button variant="contained" color="primary" startIcon={<MenuBookIcon />} onClick={(e) => setRefsAnchorEl(e.currentTarget)}>
            Довідники
          </Button>
          <Menu anchorEl={refsAnchorEl} open={refsMenuOpen} onClose={() => setRefsAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
            {[
              ['/products', 'Вироби'],
              ['/raw-materials', 'Сировина'],
              ['/dyes', 'Фарби'],
              ['/packaging-materials', 'Упаковка'],
              ['/filament', 'Філамент'],
              ['/consumable-materials', 'Розхідні матеріали'],
            ].map(([path, label]) => (
              <MenuItem
                key={path}
                onClick={() => {
                  setRefsAnchorEl(null);
                  navigateWithReturn(navigate, path, stockReturnTo('blocking'), stockReturnLabel('blocking'));
                }}
              >
                {label}
              </MenuItem>
            ))}
          </Menu>
          <Button variant="contained" color="success" startIcon={<InputIcon />}
            onClick={() => setInfoDialog({ title: 'Прихід — Блокування', text: 'Заблоковані вироби надходять на склад автоматично після фіксації у контролі якості. Ручне надходження не передбачено.' })}>
            Прихід
          </Button>
          <Button variant="contained" color="error" startIcon={<OutputIcon />}
            onClick={() => setInfoDialog({ title: 'Вибуття — Блокування', text: 'Вибуття здійснюється через кнопку "Вирішити" у рядку таблиці — допустити, списати або відправити на доробку.' })}>
            Вибуття
          </Button>
        </Box>
      </Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Заблоковану продукцію не можна переміщувати чи відвантажувати. Дії ВКЯ: розблокувати → ГП, в брак, пересортувати.
        {!canResolve ? ' Рішення доступні лише ролі ВКЯ.' : ''}
      </Alert>
      {shortageOpen && (
        <Box sx={{ mb: 1.5 }}>
          <Chip
            label="Активний фільтр: Нестача"
            color="warning"
            onDelete={() => setShortageOpen(false)}
            sx={{ fontWeight: 600 }}
          />
        </Box>
      )}
      <Collapse in={shortageOpen}>
        <Box sx={{ mb: 3, border: '1px solid', borderColor: 'warning.main', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1, bgcolor: 'warning.main' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'warning.contrastText' }}>
              ⚠️ Нестача матеріалів для запланованих виробничих замовлень
            </Typography>
          </Box>
          {shortageLoading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={28} /></Box>
          ) : shortages.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="success.main" fontWeight={600}>✅ Нестач немає</Typography>
            </Box>
          ) : (
            <TableContainer><Table size="small">
              <TableHead><TableRow>
                <TableCell>Матеріал</TableCell><TableCell align="right">Потрібно</TableCell>
                <TableCell align="right">Є на складі</TableCell><TableCell align="right">Нестача</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {shortages.map((s, i) => (
                  <TableRow key={i}>
                    <TableCell>{s.material_name || s.name}</TableCell>
                    <TableCell align="right">{s.required_quantity ?? '—'}</TableCell>
                    <TableCell align="right">{s.available_quantity ?? '—'}</TableCell>
                    <TableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>{s.shortage ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></TableContainer>
          )}
        </Box>
      </Collapse>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          label="Пошук по виробу або артикулу"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ flex: '1 1 280px' }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Фільтр (статус)</InputLabel>
          <Select
            value={statusFilter}
            label="Фільтр (статус)"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">Усі</MenuItem>
            <MenuItem value="blocked">Заблоковано</MenuItem>
            <MenuItem value="temp_released">Тимчасовий допуск</MenuItem>
            <MenuItem value="released">Допущено</MenuItem>
            <MenuItem value="scrapped">Списано</MenuItem>
            <MenuItem value="reworked">На доробку</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Сортування</InputLabel>
          <Select label="Сортування" value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
            <MenuItem value="created">За датою</MenuItem>
            <MenuItem value="name">За назвою</MenuItem>
            <MenuItem value="quantity">За кількістю</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Напрям</InputLabel>
          <Select label="Напрям" value={sortDir} onChange={(e) => setSortDir(e.target.value)}>
            <MenuItem value="asc">↑ Зростання</MenuItem>
            <MenuItem value="desc">↓ Спадання</MenuItem>
          </Select>
        </FormControl>
        <Button size="small" variant="outlined" onClick={() => {
          setSearch(''); setStatusFilter('all'); setSortKey('created'); setSortDir('desc');
        }}>
          Скинути
        </Button>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Залишки" />
          <Tab label="Журнал" />
        </Tabs>
      </Box>

      {tab === 1 && (
        journalLoading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : journalRows.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Журнал порожній
          </Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Коли</TableCell>
                  <TableCell>Хто</TableCell>
                  <TableCell>Виріб</TableCell>
                  <TableCell>Дія</TableCell>
                  <TableCell align="right">К-ть</TableCell>
                  <TableCell align="right">Придатна</TableCell>
                  <TableCell align="right">Брак</TableCell>
                  <TableCell>Чому</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {journalRows.map((j) => (
                  <TableRow key={j.id}>
                    <TableCell>{formatDt(j.created_at)}</TableCell>
                    <TableCell>{j.staff_name || '—'}</TableCell>
                    <TableCell>{j.product_name || `#${j.blocked_stock}`}</TableCell>
                    <TableCell>{j.action_display || j.action}</TableCell>
                    <TableCell align="right">{j.quantity}</TableCell>
                    <TableCell align="right">{j.quantity_good || '—'}</TableCell>
                    <TableCell align="right">{j.quantity_scrap || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 240, whiteSpace: 'pre-wrap' }}>{j.note || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}
      {tab === 0 && loading && (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      )}
      {tab === 0 && !loading && filteredRows.length === 0 && (
        <Typography color="text.secondary">Записів не знайдено.</Typography>
      )}
      {tab === 0 && !loading && filteredRows.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>№</TableCell>
                <TableCell>Виріб</TableCell>
                <TableCell>Артикул</TableCell>
                <TableCell>Замовлення</TableCell>
                <TableCell align="right">К-ть (шт)</TableCell>
                <TableCell align="right">Коробок</TableCell>
                <TableCell>Партії</TableCell>
                <TableCell>Причина</TableCell>
                <TableCell>Обсяг</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Дата</TableCell>
                <TableCell>Вирішено</TableCell>
                <TableCell>Дія</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((r) => {
                const st = STATUS_LABELS[r.status] || { label: r.status, color: 'default' };
                return (
                  <TableRow key={r.id}>
                    <TableCell>{r.id}</TableCell>
                    <TableCell>{r.product_name || '—'}</TableCell>
                    <TableCell>{r.product_article || '—'}</TableCell>
                    <TableCell>{r.production_order_number || '—'}</TableCell>
                    <TableCell align="right">{r.quantity}</TableCell>
                    <TableCell align="right">{r.rejected_boxes || 0}</TableCell>
                    <TableCell sx={{ maxWidth: 160, whiteSpace: 'pre-wrap' }}>{r.rejected_batches || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 200, whiteSpace: 'pre-wrap' }}>{r.reason || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 180 }}>
                      {(r.scopes_display || []).length
                        ? (r.scopes_display || []).map((s) => (
                          <Chip key={s} label={s} size="small" sx={{ m: 0.25 }} />
                        ))
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip label={st.label} color={st.color} size="small" />
                    </TableCell>
                    <TableCell>{formatDt(r.created_at)}</TableCell>
                    <TableCell>
                      {r.resolved_at ? (
                        <Box>
                          <Typography variant="caption" display="block">{formatDt(r.resolved_at)}</Typography>
                          <Typography variant="caption" color="text.secondary">{r.resolved_by_name || ''}</Typography>
                          {r.resolution_note && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {r.resolution_note}
                            </Typography>
                          )}
                        </Box>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      {r.status === 'blocked' && canResolve && (
                        <Button size="small" variant="outlined" onClick={() => openResolve(r)}>
                          Дія ВКЯ
                        </Button>
                      )}
                      {r.status === 'temp_released' && canResolve && !r.ship_approved_at && (
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          disabled={saving}
                          onClick={() => approveShip(r)}
                          sx={{ ml: r.status === 'blocked' ? 1 : 0 }}
                        >
                          Погодити відвантаження
                        </Button>
                      )}
                      {r.status === 'temp_released' && r.ship_approved_at && (
                        <Typography variant="caption" color="success.main">
                          Відвантаження погоджено {formatDt(r.ship_approved_at)}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={Boolean(infoDialog)} onClose={() => setInfoDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{infoDialog?.title}</DialogTitle>
        <DialogContent>
          <Typography sx={{ pt: 1 }}>{infoDialog?.text}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialog(null)}>Закрити</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(resolveDialog)} onClose={() => setResolveDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          ВКЯ — {resolveDialog?.product_name} ({resolveDialog?.quantity} шт)
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            Розблокувати → склад ГП · В брак → склад браку/дробарка · Пересортувати — розділити на придатну і брак.
            Тимчасовий допуск: відвантаження лише після окремого погодження ВКЯ.
          </Alert>
          <FormControl fullWidth size="small" margin="dense">
            <InputLabel>Дія</InputLabel>
            <Select
              value={resolveForm.action}
              label="Дія"
              onChange={(e) => setResolveForm((f) => ({ ...f, action: e.target.value }))}
            >
              <MenuItem value="unlock">Розблокувати → склад ГП</MenuItem>
              <MenuItem value="scrap">В брак</MenuItem>
              <MenuItem value="resort">Пересортувати</MenuItem>
              <MenuItem value="temp_release">Тимчасовий допуск до відвантаження</MenuItem>
            </Select>
          </FormControl>
          {resolveForm.action === 'resort' && (
            <>
              <TextField
                label="Придатна (шт)"
                type="number"
                fullWidth
                size="small"
                margin="dense"
                value={resolveForm.good_qty}
                onChange={(e) => setResolveForm((f) => ({ ...f, good_qty: e.target.value }))}
              />
              <TextField
                label="Брак (шт)"
                type="number"
                fullWidth
                size="small"
                margin="dense"
                value={resolveForm.scrap_qty}
                onChange={(e) => setResolveForm((f) => ({ ...f, scrap_qty: e.target.value }))}
                helperText={`Сума має = ${resolveDialog?.quantity || 0}`}
              />
            </>
          )}
          <TextField
            label="Чому / примітка"
            fullWidth
            size="small"
            margin="dense"
            multiline
            minRows={2}
            value={resolveForm.resolution_note}
            onChange={(e) => setResolveForm((f) => ({ ...f, resolution_note: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialog(null)}>Скасувати</Button>
          <Button variant="contained" disabled={saving} onClick={submitResolve}>
            Підтвердити
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
